import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import api from "../api/axiosConfig";
import heroPhoto from "../assets/home-hero-photo.png";
import "./Home.css";

const FALLBACK_SHOWCASE_PHOTOS = [
  "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=320&q=80",
  "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=320&q=80",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=320&q=80",
  "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?auto=format&fit=crop&w=320&q=80",
  "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=320&q=80",
  "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=320&q=80"
];

const SHOWCASE_REFRESH_INTERVAL_MS = 15000;

function Home() {
  const { user, isAuthenticated } = useAuth();
  const { t } = useI18n();
  const [showcasePhotos, setShowcasePhotos] = useState(FALLBACK_SHOWCASE_PHOTOS);

  const username = user?.username || "there";

  const loadShowcasePhotos = useCallback(async () => {
    try {
      const response = await api.get("/api/users/public/showcase-photos?count=6");
      const urls = (response.data || [])
        .map((item) => item.photoUrl)
        .filter(Boolean);

      if (urls.length > 0) {
        setShowcasePhotos(urls);
      }
    } catch (error) {
      // Keep showing whatever photos we already have (fallback or previous batch)
    }
  }, []);

  useEffect(() => {
    loadShowcasePhotos();

    const intervalId = setInterval(loadShowcasePhotos, SHOWCASE_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadShowcasePhotos]);

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-hero-content">
          <div className="home-kicker">
            {t("home.badge")}
          </div>

          <h1 className="home-title">
            {isAuthenticated
              ? t("home.welcome", { username })
              : t("home.title")}
          </h1>

          <p className="home-subtitle">
            {t("home.subtitle")}
          </p>

          <div className="home-actions">
            {isAuthenticated ? (
              <>
                <Link to="/browse" className="home-primary-button">
                  {t("home.browse")}
                </Link>

                <Link to="/matches" className="home-secondary-button">
                  {t("home.viewMatches")}
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" className="home-primary-button">
                  {t("home.createAccount")}
                </Link>

                <Link to="/login" className="home-secondary-button">
                  {t("auth.login")}
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="home-hero-photo">
          <img
            src={heroPhoto}
            alt={t("home.meetPeopleAlt")}
            className="home-hero-photo-img"
          />

          <div className="home-hero-photo-gradient"></div>

          <div className="home-hero-photo-card">
            <div className="preview-badge">{t("home.newMatch")}</div>
            <h2>{t("home.findSpark")}</h2>
            <p>{t("home.previewDescription")}</p>
            <div className="preview-tags">
              <span>{t("home.interestMusic")}</span>
              <span>{t("home.interestTravel")}</span>
              <span>{t("home.interestMovies")}</span>
            </div>
          </div>

          <div className="preview-buttons">
            <span className="preview-pass">×</span>
            <span className="preview-like">♥</span>
          </div>
        </div>
      </section>

      <section className="home-photo-strip">
        {showcasePhotos.map((src, index) => (
          <div className="home-photo-strip-item" key={`${src}-${index}`}>
            <img src={src} alt={t("home.memberAlt", { number: index + 1 })} />
            <span className="home-photo-strip-online" />
          </div>
        ))}
      </section>

      <section className="home-info-section">
        <div className="home-info-card">
          <h2>{t("home.howItWorks")}</h2>

          <div className="home-steps">
            <div className="home-step">
              <span>1</span>

              <div>
                <h3>{t("home.browseStep")}</h3>
                <p>{t("home.browseStepDescription")}</p>
              </div>
            </div>

            <div className="home-step">
              <span>2</span>

              <div>
                <h3>{t("home.matchStep")}</h3>
                <p>{t("home.matchStepDescription")}</p>
              </div>
            </div>

            <div className="home-step">
              <span>3</span>

              <div>
                <h3>{t("home.chatStep")}</h3>
                <p>{t("home.chatStepDescription")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
